import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  IoDocumentTextOutline,
  IoFingerPrintOutline,
  IoShareSocialOutline,
  IoShieldCheckmarkOutline,
  IoOptionsOutline,
  IoMailOutline,
  IoLockClosedOutline,
} from "react-icons/io5";
import { useI18n } from "../contexts/I18n";
import { marketingI18n } from "../content/homeMarketing";
import useScrollAnimate from "../hooks/useScrollAnimate";
import MarketingPageIntro from "./marketing/MarketingPageIntro";
import "./Home.css";
import "./MarketingPages.css";
import "./PrivacyPage.css";

const LAST_UPDATED = "2025-07-11";
const LAST_UPDATED_DISPLAY = "11 Jul 2025";

const SUMMARY_ICONS = {
  lock: IoLockClosedOutline,
  shield: IoShieldCheckmarkOutline,
  clients: IoFingerPrintOutline,
};

export default function PrivacyPolicy() {
  const { t, lang } = useI18n();
  const pageT = marketingI18n[lang].pages.privacy;
  useScrollAnimate([lang]);

  const sections = useMemo(
    () => [
      {
        id: "collect",
        icon: IoDocumentTextOutline,
        title: t("ppWhatWeCollect"),
        items: [
          t("ppCollectAccountInfo"),
          t("ppCollectBookings"),
          t("ppCollectUsageData"),
        ],
        listStyle: "dot",
      },
      {
        id: "use",
        icon: IoFingerPrintOutline,
        title: t("ppHowWeUse"),
        items: [
          t("ppUseProvideImprove"),
          t("ppUseProcess"),
          t("ppUsePreventFraud"),
          t("ppUseComply"),
        ],
        listStyle: "dot",
      },
      {
        id: "sharing",
        icon: IoShareSocialOutline,
        title: t("ppSharing"),
        intro: t("ppSharingIntro"),
        items: [
          t("ppSharingProviders"),
          t("ppSharingBooked"),
          t("ppSharingAuthorities"),
        ],
        listStyle: "dot",
      },
      {
        id: "security",
        icon: IoShieldCheckmarkOutline,
        title: t("ppSecurity"),
        body: t("ppSecurityText"),
      },
      {
        id: "choices",
        icon: IoOptionsOutline,
        title: t("ppYourChoices"),
        items: [
          t("ppChoiceUpdateProfile"),
          t("ppChoiceUnsubscribe"),
          t("ppChoiceRequestDeletion"),
          t("ppChoiceControlCookies"),
        ],
        listStyle: "dot",
      },
      {
        id: "contact",
        icon: IoMailOutline,
        title: t("contact"),
        isContact: true,
      },
    ],
    [t]
  );

  return (
    <div
      className={`marketing-page privacy-page ${lang === "ar" ? "rtl" : ""}`}
      dir={lang === "ar" ? "rtl" : "ltr"}
    >
      <MarketingPageIntro variant="privacy" page={pageT} lang={lang} layout="split" />

      <section className="privacy-summary-section" aria-label={pageT.sectionsLabel}>
        <div className="container">
          <ul className="privacy-summary-grid">
            {pageT.summary.map((item, idx) => {
              const Icon = SUMMARY_ICONS[item.icon] || IoShieldCheckmarkOutline;
              return (
                <li
                  key={item.title}
                  className={`privacy-summary-card scroll-animate from-bottom delay-${Math.min(idx + 1, 4)}`}
                >
                  <span className="privacy-summary-card__icon" aria-hidden="true">
                    <Icon />
                  </span>
                  <h2 className="privacy-summary-card__title">{item.title}</h2>
                  <p className="privacy-summary-card__desc">{item.desc}</p>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      <section className="privacy-policy-section" aria-labelledby="privacy-policy-heading">
        <div className="container">
          <header className="privacy-policy-header scroll-animate from-bottom">
            <div className="privacy-policy-header__copy">
              <h2 id="privacy-policy-heading" className="privacy-policy-header__title">
                {t("privacy")}
              </h2>
              <p className="privacy-policy-header__lead">{t("privacyLead")}</p>
            </div>
            <p className="privacy-policy-header__meta">
              {t("effectiveLabel")}:{" "}
              <time dateTime={LAST_UPDATED}>{LAST_UPDATED_DISPLAY}</time>
            </p>
          </header>

          <div className="privacy-layout">
            <nav className="privacy-toc scroll-animate from-bottom delay-1" aria-label={pageT.tocLabel}>
              <h3 className="privacy-toc__label">{pageT.tocLabel}</h3>
              <ol className="privacy-toc__list">
                {sections.map((section) => (
                  <li key={section.id}>
                    <a href={`#privacy-${section.id}`} className="privacy-toc__link">
                      {section.title}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>

            <div className="privacy-sections">
              {sections.map((section, idx) => {
                const Icon = section.icon;
                return (
                  <article
                    key={section.id}
                    id={`privacy-${section.id}`}
                    className={`privacy-section-card scroll-animate from-bottom delay-${Math.min(idx + 1, 6)}`}
                  >
                    <header className="privacy-section-card__header">
                      <span className="privacy-section-card__icon" aria-hidden="true">
                        <Icon />
                      </span>
                      <h3 className="privacy-section-card__title">{section.title}</h3>
                    </header>

                    {section.intro && (
                      <p className="privacy-section-card__intro">{section.intro}</p>
                    )}

                    {section.body && (
                      <p className="privacy-section-card__body">{section.body}</p>
                    )}

                    {section.items && (
                      <ul className={`privacy-section-card__list privacy-section-card__list--${section.listStyle}`}>
                        {section.items.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    )}

                    {section.isContact && (
                      <address className="privacy-section-card__contact">
                        <span>Ehgezly Privacy Team, Cairo, Egypt</span>
                        <a href="mailto:bookwithehgezly@gmail.com">bookwithehgezly@gmail.com</a>
                      </address>
                    )}
                  </article>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="privacy-cta-section">
        <div className="container privacy-cta-inner scroll-animate from-bottom delay-2">
          <h2 className="privacy-cta-title">{pageT.ctaTitle}</h2>
          <p className="privacy-cta-desc">{pageT.ctaDesc}</p>
          <div className="privacy-cta-actions">
            <Link to={pageT.ctaTo} className="privacy-cta-btn privacy-cta-btn--primary">
              {pageT.cta}
            </Link>
            <Link to={pageT.ctaSecondaryTo} className="privacy-cta-btn privacy-cta-btn--secondary">
              {pageT.ctaSecondary}
            </Link>
          </div>
        </div>
      </section>

      <div className="section-wave section-wave--light" aria-hidden="true" />
    </div>
  );
}
